import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SURVEY_URL } from '@insite-store360/shared';
import { catchError, map, Observable, of } from 'rxjs';
import { AsyncExportService } from './async-export.service';
import { ErrorHandlerService } from './error-handler.service';

export interface NonMovingStockFilter {
  regionId?: string | number | any[];
  countryId?: string | number | any[];
  tds?: string | number;
  tdsId?: number | string;
  userId?: number | string;
  account?: string | number;
  accountId?: number | string | any[];
  storeId?: number | string | any[];
  month?: string;
  year?: string | number;
  search?: string;
  lang?: 'th' | 'en';
  pageIndex?: number;
  pageSize?: number;
}

export interface NonMovingStockSummary {
  totalStores: number;
  totalItems?: number;
  targetAchievedCount: number;
  slowClearanceCount: number;
  overallClearancePercent: number;
  clearedAmount: number;
  totalStockAmount: number;
  remainingAmount: number;
  targetPercent: number;
  storeParticipationPct: number;
  targetAchievedRatePct: number;
  delayRiskPct: number;
  overallVsTargetPct: number;
  achievedCount: number;
  inProgressCount: number;
  notStartedCount: number;
  currency?: string;
  currencySymbol?: string;
  lastUpdated?: string;
}

export interface StoreItem {
  id: string;
  storeId?: number | string;
  accountId?: number | string;
  accountName?: string;
  countryId?: number | string;
  countryName?: string;
  regionId?: number | string;
  month?: string;
  year?: number | string;
  model?: string;
  raw?: any;
  storeCode: string;
  storeName: string;
  promoterName: string;
  account: string;
  tdsArea: string;
  value: number;
  category?: string;
  categories: string[];
  progress: number;
  lastClearedTh: string;
  lastClearedEn: string;
  daysAgo: number;
  status: 'achieved' | 'in_progress' | 'not_started';
}

export interface TdsGroupItem {
  id?: number;
  tdsId?: number;
  userId?: number;
  name: string;
  userName?: string;
  currency?: string;
  progress: number;
  stores: number;
  targetReached: number;
  cleared: number;
  total: number;
  totalStores?: number;
  clearedStock?: number;
  clearedStockQuantity?: number;
  clearedStockAmount?: number;
  clearedAmount?: number;
  defectiveStock?: number;
  defectiveStockQuantity?: number;
  defectiveStockAmount?: number;
  pendingStock?: number;
  pendingStockQuantity?: number;
  pendingStockAmount?: number;
  notClearedAmount?: number;
  totalStock?: number;
  totalQuantity?: number;
  totalAmount?: number;
}

export interface AccountGroupItem {
  id?: number;
  accountId?: number;
  name: string;
  accountName?: string;
  currency?: string;
  progress: number;
  stores: number;
  targetReached: number;
  cleared: number;
  total: number;
  clearedStock?: number;
  clearedStockQuantity?: number;
  clearedStockAmount?: number;
  defectiveStock?: number;
  defectiveStockQuantity?: number;
  defectiveStockAmount?: number;
  pendingStock?: number;
  pendingStockQuantity?: number;
  pendingStockAmount?: number;
  notClearedAmount?: number;
  totalStock?: number;
  totalQuantity?: number;
  totalAmount?: number;
}

export interface CategoryGroupItem {
  id?: number;
  categoryId?: number;
  code: string;
  name: string;
  categoryName?: string;
  percent: number;
  progress?: number;
  cleared: number;
  total: number;
  stores?: number;
  totalStores?: number;
  targetReached?: number;
  storesLabelEn?: string;
  storesLabelTh?: string;
  currency?: string;
  clearedStock?: number;
  clearedStockQuantity?: number;
  clearedStockAmount?: number;
  defectiveStock?: number;
  defectiveStockQuantity?: number;
  defectiveStockAmount?: number;
  pendingStock?: number;
  pendingStockQuantity?: number;
  pendingStockAmount?: number;
  notClearedAmount?: number;
  totalStock?: number;
  totalQuantity?: number;
  totalAmount?: number;
}

export interface CategorySummary {
  code: string;
  name: string;
  percent: number;
  cleared: number;
  total: number;
  currency?: string;
}

export interface NonMovingStockFilterOptions {
  tdsListEn: string[];
  tdsListTh: string[];
  accountListEn: string[];
  accountListTh: string[];
  monthListEn: string[];
  monthListTh: string[];
  yearListEn: string[];
  yearListTh: string[];
}

@Injectable({
  providedIn: 'root',
})
export class NonMovingStockService {
  /**
   * Set this to `false` to consume real backend APIs.
   * Built-in fallback to mock data is provided if an endpoint is unreachable.
   */
  public useMockData = false;

  private readonly SURVEY_SERVICE_URL = `${SURVEY_URL}`;

  // Real backend API endpoint paths
  private readonly ENDPOINTS = {
    // API 1: Overview KPI Cards
    overviewKpiCards: `${this.SURVEY_SERVICE_URL}/notMovingStock/getOverviewKpiCards`,
    summary: `${this.SURVEY_SERVICE_URL}/notMovingStock/getOverviewKpiCards`,

    // API 2: By Account Summary
    byAccountSummary: `${this.SURVEY_SERVICE_URL}/notMovingStock/getByAccountSummary`,
    byAccount: `${this.SURVEY_SERVICE_URL}/notMovingStock/getByAccountSummary`,

    // API 3: Non Moving By TDS
    nonMovingByTds: `${this.SURVEY_SERVICE_URL}/notMovingStock/getNonMovingByTDS`,
    byTds: `${this.SURVEY_SERVICE_URL}/notMovingStock/getNonMovingByTDS`,

    // Additional endpoints
    stores: `${this.SURVEY_SERVICE_URL}/notMovingStock/getAllQualityResult`,
    byCategory: `${this.SURVEY_SERVICE_URL}/notMovingStock/getByCategorySummary`,
    categoryBreakdown: `${this.SURVEY_SERVICE_URL}/notMovingStock/getCategoryBreakdown`,
    filterOptions: `${this.SURVEY_SERVICE_URL}/notMovingStock/filter-options`,
    export: `${this.SURVEY_SERVICE_URL}/notMovingStock/downloadExcelTemplate`,
  };

  // ===================== MOCK DATA SETS =====================

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService,
    private asyncExportService: AsyncExportService,
  ) { }

  // ===================== SERVICE METHODS =====================

  private readonly THAI_TO_ENG_MONTHS: Record<string, string> = {
    'มกราคม': 'January',
    'กุมภาพันธ์': 'February',
    'มีนาคม': 'March',
    'เมษายน': 'April',
    'พฤษภาคม': 'May',
    'มิถุนายน': 'June',
    'กรกฎาคม': 'July',
    'สิงหาคม': 'August',
    'กันยายน': 'September',
    'ตุลาคม': 'October',
    'พฤศจิกายน': 'November',
    'ธันวาคม': 'December',
  };

  /**
   * Helper to normalize month names to English
   */
  private normalizeMonth(month?: string): string | undefined {
    if (!month || this.isAllFilter(month)) return undefined;
    const trimmed = month.trim();
    return this.THAI_TO_ENG_MONTHS[trimmed] || trimmed;
  }

  /**
   * Helper to build HttpParams from filters
   */
  private buildFilterParams(filters?: NonMovingStockFilter): HttpParams {
    let params = new HttpParams();
    if (filters) {
      const rawRegion = filters.regionId !== undefined && filters.regionId !== null ? filters.regionId : (filters as any).region;
      if (rawRegion !== undefined && rawRegion !== null && !this.isAllFilter(rawRegion.toString())) {
        const val = Array.isArray(rawRegion) ? rawRegion.filter(Boolean).join(',') : rawRegion.toString();
        if (val) {
          params = params.set('regionId', val);
          params = params.set('region', val);
        }
      }

      const rawCountry = filters.countryId !== undefined && filters.countryId !== null ? filters.countryId : (filters as any).country;
      if (rawCountry !== undefined && rawCountry !== null && !this.isAllFilter(rawCountry.toString())) {
        const val = Array.isArray(rawCountry) ? rawCountry.filter(Boolean).join(',') : rawCountry.toString();
        if (val) {
          params = params.set('countryId', val);
          params = params.set('country', val);
        }
      }

      // 1. TDS / User filter
      if (filters.userId && !this.isAllFilter(filters.userId.toString())) {
        const val = Array.isArray(filters.userId) ? filters.userId.filter(Boolean).join(',') : filters.userId.toString();
        if (val) {
          params = params.set('userId', val);
          params = params.set('tdsId', val);
        }
      } else if (filters.tdsId && !this.isAllFilter(filters.tdsId.toString())) {
        const val = Array.isArray(filters.tdsId) ? filters.tdsId.filter(Boolean).join(',') : filters.tdsId.toString();
        if (val) {
          params = params.set('userId', val);
          params = params.set('tdsId', val);
        }
      } else if (filters.tds && !this.isAllFilter(filters.tds.toString())) {
        const val = Array.isArray(filters.tds) ? filters.tds.filter(Boolean).join(',') : filters.tds.toString();
        if (val) {
          params = params.set('userId', val);
          params = params.set('tdsId', val);
        }
      }

      // 2. Account filter
      if (filters.accountId !== undefined && filters.accountId !== null && !this.isAllFilter(filters.accountId.toString())) {
        const val = Array.isArray(filters.accountId) ? filters.accountId.filter(Boolean).join(',') : filters.accountId.toString();
        if (val) {
          params = params.set('accountId', val);
          params = params.set('account', val);
        }
      } else if (filters.account && !this.isAllFilter(filters.account.toString())) {
        const val = Array.isArray(filters.account) ? filters.account.filter(Boolean).join(',') : filters.account.toString();
        if (val) {
          params = params.set('accountId', val);
          params = params.set('account', val);
        }
      }

      // Store filter
      if (filters.storeId !== undefined && filters.storeId !== null && !this.isAllFilter(filters.storeId.toString())) {
        const val = Array.isArray(filters.storeId) ? filters.storeId.filter(Boolean).join(',') : filters.storeId.toString();
        if (val) {
          params = params.set('storeId', val);
        }
      }

      // 3. Month filter (normalized to English, omitted if 'All')
      const normalizedMonth = this.normalizeMonth(filters.month);
      if (normalizedMonth) {
        params = params.set('month', normalizedMonth);
      }

      // 4. Year filter (omitted if 'All')
      if (filters.year && !this.isAllFilter(filters.year.toString())) {
        const y = parseInt(filters.year.toString(), 10);
        if (!isNaN(y)) {
          params = params.set('year', y.toString());
        }
      }

      // 5. Search
      if (filters.search && filters.search.trim()) {
        params = params.set('search', filters.search.trim());
      }

      // 6. Lang
      if (filters.lang) {
        params = params.set('lang', filters.lang);
      }

      // 7. Pagination
      if (filters.pageIndex !== undefined) {
        params = params.set('pageIndex', filters.pageIndex.toString());
      }
      if (filters.pageSize !== undefined) {
        params = params.set('pageSize', filters.pageSize.toString());
      }
    }
    return params;
  }

  /**
   * Helper to check if filter matches "All"
   */
  private isAllFilter(val?: string | number): boolean {
    if (val === undefined || val === null) return true;
    const str = val.toString();
    return !str || str === 'All' || str === 'ทั้งหมด';
  }

  /**
   * Mapper: Overview KPI Cards response (API 1)
   */
  private mapOverviewKpiResponse(res: any): NonMovingStockSummary {
    const raw = res?.result?.result ?? res?.result ?? res;
    if (!raw) {
      return {} as NonMovingStockSummary;
    }

    if (raw.totalStores !== undefined && raw.overallClearancePercent !== undefined && !raw.allStores) {
      return raw as NonMovingStockSummary;
    }

    const allStores = raw.allStores || {};
    const reachTarget = raw.reachTarget || {};
    const slowClearance = raw.slowClearance || {};
    const overviewClear = raw.overviewClear || {};

    const totalStores = Number(allStores.totalStores ?? 0);
    const totalItems = Number(allStores.totalItems ?? 0);
    const targetAchievedCount = Number(reachTarget.targetStores ?? 0);
    const slowClearanceCount = Number(slowClearance.slowClearanceStores ?? 0);
    const clearancePercentage = Number(overviewClear.clearancePercentage ?? 0);
    const clearedAmount = Number(overviewClear.clearedAmount ?? 0);
    const totalAmount = Number(overviewClear.totalAmount ?? 0);
    const currency = overviewClear.currency || 'THB';
    const currencySymbol = overviewClear.currencySymbol || '$';
    const targetThreshold = Number(reachTarget.targetThreshold ?? 30);
    const remainingAmount = Math.max(0, totalAmount - clearedAmount);

    const storeParticipationPct = 100;
    const targetAchievedRatePct =
      totalStores > 0
        ? Number(((targetAchievedCount / totalStores) * 100).toFixed(1))
        : 0;
    const delayRiskPct =
      totalStores > 0 ? Number(((slowClearanceCount / totalStores) * 100).toFixed(1)) : 0;
    const overallVsTargetPct =
      targetThreshold > 0 ? Number(((clearancePercentage / targetThreshold) * 100).toFixed(1)) : 0;

    const storeStatus = raw.storeStatus || {};
    const achievedCount = Number(storeStatus.achievedCount ?? targetAchievedCount);
    const inProgressCount = Number(
      storeStatus.inProgressCount ?? (totalStores > 0 ? Math.max(0, totalStores - achievedCount - slowClearanceCount) : 0)
    );
    const notStartedCount = Number(
      storeStatus.notStartedCount ?? (totalStores > 0 ? Math.max(0, totalStores - achievedCount - inProgressCount) : 0)
    );

    return {
      totalStores,
      totalItems,
      targetAchievedCount,
      slowClearanceCount,
      overallClearancePercent: clearancePercentage,
      clearedAmount,
      totalStockAmount: totalAmount,
      remainingAmount,
      targetPercent: targetThreshold,
      storeParticipationPct,
      targetAchievedRatePct,
      delayRiskPct,
      overallVsTargetPct,
      achievedCount,
      inProgressCount,
      notStartedCount,
      currency,
      currencySymbol,
      lastUpdated: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
  }

  /**
   * Mapper: By Account Summary response (API 2)
   */
  private mapAccountSummaryResponse(res: any): AccountGroupItem[] {
    const raw = res?.result?.result ?? res?.result ?? res;
    const accounts: any[] = raw?.accounts ?? (Array.isArray(raw) ? raw : []);

    if (!accounts || accounts.length === 0) {
      return [];
    }

    return accounts.map((acc: any) => {
      const totalAmount = Number(acc.totalAmount ?? 0);
      const clearedStockAmount = Number(acc.clearedStockAmount ?? acc.clearedAmount ?? 0);
      const progress =
        totalAmount > 0 ? Number(((clearedStockAmount / totalAmount) * 100).toFixed(1)) : 0;

      return {
        id: acc.accountId,
        accountId: acc.accountId,
        name: acc.accountName || `Account ${acc.accountId}`,
        accountName: acc.accountName || `Account ${acc.accountId}`,
        currency: acc.currency,
        stores: Number(acc.totalStock ?? acc.totalStores ?? 1),
        targetReached: Number(acc.clearedStock ?? (progress >= 30 ? 1 : 0)),
        cleared: clearedStockAmount,
        total: totalAmount,
        progress,
        clearedStock: Number(acc.clearedStock ?? 0),
        clearedStockQuantity: Number(acc.clearedStockQuantity ?? 0),
        clearedStockAmount,
        defectiveStock: Number(acc.defectiveStock ?? 0),
        defectiveStockQuantity: Number(acc.defectiveStockQuantity ?? 0),
        defectiveStockAmount: Number(acc.defectiveStockAmount ?? 0),
        pendingStock: Number(acc.pendingStock ?? 0),
        pendingStockQuantity: Number(acc.pendingStockQuantity ?? 0),
        pendingStockAmount: Number(acc.pendingStockAmount ?? 0),
        notClearedAmount: Number(acc.notClearedAmount ?? 0),
        totalStock: Number(acc.totalStock ?? 0),
        totalQuantity: Number(acc.totalQuantity ?? 0),
        totalAmount,
      };
    });
  }

  /**
   * Mapper: TDS Summary response (API 3)
   */
  private mapTdsSummaryResponse(res: any): TdsGroupItem[] {
    const raw = res?.result?.result ?? res?.result ?? res;
    const tdsList: any[] = raw?.tdsList ?? (Array.isArray(raw) ? raw : []);

    if (!tdsList || tdsList.length === 0) {
      return [];
    }

    return tdsList.map((tds: any) => {
      const totalAmount = Number(tds.totalAmount ?? 0);
      const clearedAmount = Number(tds.clearedStockAmount ?? tds.clearedAmount ?? 0);
      const progress =
        totalAmount > 0 ? Number(((clearedAmount / totalAmount) * 100).toFixed(1)) : 0;

      return {
        id: tds.tdsId ?? tds.userId,
        tdsId: tds.tdsId,
        userId: tds.userId,
        name: tds.userName || `TDS ${tds.tdsId}`,
        userName: tds.userName || `TDS ${tds.tdsId}`,
        currency: tds.currency,
        stores: Number(tds.totalStores ?? tds.totalStock ?? 1),
        totalStores: Number(tds.totalStores ?? tds.totalStock ?? 1),
        targetReached: Number(tds.clearedStock ?? (progress >= 30 ? 1 : 0)),
        cleared: clearedAmount,
        total: totalAmount,
        progress,
        clearedStock: Number(tds.clearedStock ?? 0),
        clearedStockQuantity: Number(tds.clearedStockQuantity ?? 0),
        clearedStockAmount: clearedAmount,
        clearedAmount,
        defectiveStock: Number(tds.defectiveStock ?? 0),
        defectiveStockQuantity: Number(tds.defectiveStockQuantity ?? 0),
        defectiveStockAmount: Number(tds.defectiveStockAmount ?? 0),
        pendingStock: Number(tds.pendingStock ?? 0),
        pendingStockQuantity: Number(tds.pendingStockQuantity ?? 0),
        pendingStockAmount: Number(tds.pendingStockAmount ?? 0),
        notClearedAmount: Number(tds.notClearedAmount ?? 0),
        totalStock: Number(tds.totalStock ?? 0),
        totalQuantity: Number(tds.totalQuantity ?? 0),
        totalAmount,
      };
    });
  }

  /**
   * Mapper: By Category Summary response
   */
  private mapCategoryGroupsResponse(res: any): CategoryGroupItem[] {
    const raw = res?.result?.result ?? res?.result ?? res;
    const categories: any[] = raw?.categories ?? (Array.isArray(raw) ? raw : []);

    if (!categories || categories.length === 0) {
      return [];
    }

    return categories.map((cat: any) => {
      const totalAmount = Number(cat.totalAmount ?? cat.total ?? 0);
      const clearedStockAmount = Number(cat.clearedStockAmount ?? cat.cleared ?? 0);
      const defectiveStockAmount = Number(cat.defectiveStockAmount ?? 0);
      const pendingStockAmount = Number(cat.pendingStockAmount ?? 0);
      const notClearedAmount = Number(cat.notClearedAmount ?? (defectiveStockAmount + pendingStockAmount));
      const percent = Number(cat.percent ?? cat.progress ?? (totalAmount > 0 ? Number(((clearedStockAmount / totalAmount) * 100).toFixed(1)) : 0));
      const stores = Number(cat.totalStores ?? cat.stores ?? 0);
      const code = cat.code || (cat.name ? cat.name.substring(0, 2).toUpperCase() : 'OT');

      return {
        id: cat.categoryId,
        categoryId: cat.categoryId,
        code,
        name: cat.name || cat.categoryName || code,
        categoryName: cat.categoryName || cat.name || code,
        percent,
        progress: percent,
        cleared: clearedStockAmount,
        total: totalAmount,
        stores,
        totalStores: stores,
        targetReached: Number(cat.targetReached ?? (percent >= 30 ? stores : 0)),
        storesLabelEn: cat.storesLabelEn || `${stores} stores`,
        storesLabelTh: cat.storesLabelTh || `${stores} ร้าน`,
        currency: cat.currency || 'THB',
        clearedStock: Number(cat.clearedStock ?? 0),
        clearedStockQuantity: Number(cat.clearedStockQuantity ?? 0),
        clearedStockAmount,
        defectiveStock: Number(cat.defectiveStock ?? 0),
        defectiveStockQuantity: Number(cat.defectiveStockQuantity ?? 0),
        defectiveStockAmount,
        pendingStock: Number(cat.pendingStock ?? 0),
        pendingStockQuantity: Number(cat.pendingStockQuantity ?? 0),
        pendingStockAmount,
        notClearedAmount,
        totalStock: Number(cat.totalStock ?? 0),
        totalQuantity: Number(cat.totalQuantity ?? 0),
        totalAmount,
      };
    });
  }

  /**
   * Mapper: Category Breakdown response
   */
  private mapCategoryBreakdownResponse(res: any): CategorySummary[] {
    const raw = res?.result?.result ?? res?.result ?? res;
    const categories: any[] = raw?.categories ?? (Array.isArray(raw) ? raw : []);

    if (!categories || categories.length === 0) {
      return [];
    }

    return categories.map((cat: any) => ({
      code: cat.code || (cat.name ? cat.name.substring(0, 2).toUpperCase() : 'OT'),
      name: cat.name || cat.categoryName || cat.code || 'Unassigned',
      percent: Number(cat.percent ?? cat.progress ?? (cat.total > 0 ? Number(((cat.cleared / cat.total) * 100).toFixed(1)) : 0)),
      cleared: Number(cat.cleared ?? cat.clearedStockAmount ?? 0),
      total: Number(cat.total ?? cat.totalAmount ?? 0),
      currency: cat.currency || 'THB',
    }));
  }

  /**
   * 1. Get Top KPIs & Overall Clearance Summary (API 1: getOverviewKpiCards)
   */
  public getDashboardSummary(filters?: NonMovingStockFilter): Observable<NonMovingStockSummary> {
    if (this.useMockData) {
      return of({} as NonMovingStockSummary);
    }

    const params = this.buildFilterParams(filters);
    return this.http
      .get<any>(this.ENDPOINTS.overviewKpiCards, { params })
      .pipe(
        map((res) => this.mapOverviewKpiResponse(res)),
        catchError((err) => {
          console.warn('API getOverviewKpiCards call failed, falling back to mock data:', err);
          return of({} as NonMovingStockSummary);
        })
      );
  }

  /**
   * Explicit alias for API 1: getOverviewKpiCards
   */
  public getOverviewKpiCards(filters?: NonMovingStockFilter): Observable<NonMovingStockSummary> {
    return this.getDashboardSummary(filters);
  }

  /**
   * Mapper: Store Records from getAll (Backend API)
   */
  private mapStoreRecordsResponse(res: any): { items: StoreItem[], totalCount: number } {
    const raw = res?.result?.result ?? res?.result ?? res;
    const records: any[] = Array.isArray(raw) ? raw : (raw?.records ?? raw?.items ?? raw?.results ?? []);
    const totalCount = Number(res?.paginatorValue?.totalData ?? res?.totalCount ?? raw?.totalCount ?? records.length);

    if (!records || records.length === 0) {
      return { items: [], totalCount: 0 };
    }

    const items = records.map((r: any, idx: number) => {
      const storeObj = r.store || {};
      const storeName = storeObj.storeName || storeObj.name || r.storeName || `Store #${r.storeId || storeObj.id || idx + 1}`;
      const storeCode = storeObj.storeCode || storeObj.code || r.storeCode || String(r.storeId || storeObj.id || idx + 1);
      const promoterName = r.submittedByUser?.profile
        ? `${r.submittedByUser.profile.firstName || ''} ${r.submittedByUser.profile.lastName || ''}`.trim()
        : (r.submittedByUser?.userName || r.promoterName || '-');
      const accountName = (r.accountName && r.accountName !== '--' ? r.accountName : '') ||
        storeObj.account?.name ||
        storeObj.accountName ||
        (typeof r.account === 'string' && r.account !== '-' ? r.account : '') ||
        (r.account?.name ? r.account.name : '') ||
        '-';
      const account = accountName;
      const accountId = r.accountId || storeObj.accountId || storeObj.account?.id || storeObj.account_id || r.store?.accountId || r.store?.account_id;
      const countryId = r.countryId || storeObj.countryId || storeObj.country?.id || storeObj.country_id || r.store?.countryId || r.store?.country_id;
      const countryName = (r.countryName && r.countryName !== '--' ? r.countryName : '') || storeObj.country?.name || storeObj.countryName || '';
      const regionId = r.regionId || storeObj.regionId || storeObj.region?.id || storeObj.region_id || r.store?.regionId || r.store?.region_id;
      const storeId = r.storeId || storeObj.id || r.id;
      const tdsArea = storeObj.region?.name || r.regionName || r.tdsArea || storeObj.country?.name || '-';
      const value = Number(r.amount ?? r.value ?? r.price ?? 0);
      const isCleared = String(r.status).toLowerCase().includes('cleared') || r.status === 'true';
      const isPending = String(r.status).toLowerCase().includes('pending') || r.status === null;
      const progress = isCleared ? 100 : (isPending ? 0 : 100);
      const status: 'achieved' | 'in_progress' | 'not_started' = isCleared ? 'achieved' : (isPending ? 'not_started' : 'in_progress');
      const category = r.categoryName || '--'

      const updatedAtDate = r.updatedAt ? new Date(r.updatedAt) : null;
      let daysAgo = 2;
      if (updatedAtDate && !isNaN(updatedAtDate.getTime())) {
        const diffDays = Math.floor(Math.abs(Date.now() - updatedAtDate.getTime()) / (1000 * 60 * 60 * 24));
        daysAgo = diffDays;
      }
      const lastClearedEn = daysAgo === 0 ? 'Today' : (daysAgo === 1 ? '1 day ago' : `${daysAgo} days ago`);
      const lastClearedTh = daysAgo === 0 ? 'วันนี้' : (daysAgo === 1 ? '1 วันที่แล้ว' : `${daysAgo} วันที่แล้ว`);

      return {
        id: String(storeId || idx + 1),
        storeId,
        accountId,
        accountName,
        countryId,
        countryName,
        regionId,
        month: r.nmPeriod || r.month,
        year: r.year,
        model: r.model || r.raw?.model || '',
        raw: r,
        storeCode,
        storeName,
        promoterName,
        account,
        tdsArea,
        value,
        category,
        categories: [r.category || (r.model ? r.model.substring(0, 2).toUpperCase() : 'SA')],
        progress,
        lastClearedTh,
        lastClearedEn,
        daysAgo,
        status,
      };
    });

    return { items, totalCount };
  }

  /**
   * Alias: Get Not Moving Stock list for table
   */
  public getAllNotMovingStock(filters?: NonMovingStockFilter): Observable<{ items: StoreItem[], totalCount: number }> {
    return this.getStoreClearanceList(filters);
  }

  /**
   * 2. Get Stores List for Table (Filtered dynamically - connects to backend getAll)
   */
  public getStoreClearanceList(filters?: NonMovingStockFilter): Observable<{ items: StoreItem[], totalCount: number }> {
    if (this.useMockData) {
      let filtered: StoreItem[] = [];

      if (filters) {
        if (!this.isAllFilter(filters.tds)) {
          const tLower = filters.tds!.toString().toLowerCase();
          filtered = filtered.filter(
            (s) => s.tdsArea.toLowerCase().includes(tLower) || tLower.includes(s.tdsArea.toLowerCase())
          );
        }

        if (!this.isAllFilter(filters.account)) {
          const aLower = filters.account!.toString().toLowerCase();
          filtered = filtered.filter(
            (s) => s.account.toLowerCase().includes(aLower) || aLower.includes(s.account.toLowerCase())
          );
        }

        if (filters.search && filters.search.trim()) {
          const q = filters.search.toLowerCase().trim();
          filtered = filtered.filter(
            (s) =>
              s.storeName.toLowerCase().includes(q) ||
              s.promoterName.toLowerCase().includes(q) ||
              s.storeCode.toLowerCase().includes(q) ||
              s.account.toLowerCase().includes(q)
          );
        }
      }

      return of({ items: filtered, totalCount: filtered.length });
    }

    const params = this.buildFilterParams(filters);
    return this.http
      .get<any>(this.ENDPOINTS.stores, { params })
      .pipe(
        map((res) => this.mapStoreRecordsResponse(res)),
        catchError((err) => {
          console.warn('API getAll stores call failed, falling back to mock stores:', err);
          return of({ items: [], totalCount: 0 });
        })
      );
  }

  /**
   * 3. Get By TDS Groups Data (API 3: getNonMovingByTDS)
   */
  public getTdsGroups(filters?: NonMovingStockFilter): Observable<TdsGroupItem[]> {
    if (this.useMockData) {
      let filtered: TdsGroupItem[] = [];
      if (filters?.search && filters.search.trim()) {
        const q = filters.search.toLowerCase().trim();
        filtered = filtered.filter((g) => g.name.toLowerCase().includes(q));
      }
      return of(filtered);
    }

    const params = this.buildFilterParams(filters);
    return this.http
      .get<any>(this.ENDPOINTS.nonMovingByTds, { params })
      .pipe(
        map((res) => this.mapTdsSummaryResponse(res)),
        catchError((err) => {
          console.warn('API getNonMovingByTDS call failed, falling back to mock data:', err);
          return of([]);
        })
      );
  }

  /**
   * Explicit alias for API 3: getNonMovingByTDS
   */
  public getNonMovingByTDS(filters?: NonMovingStockFilter): Observable<TdsGroupItem[]> {
    return this.getTdsGroups(filters);
  }

  /**
   * 4. Get By Account Groups Data (API 2: getByAccountSummary)
   */
  public getAccountGroups(filters?: NonMovingStockFilter): Observable<AccountGroupItem[]> {
    if (this.useMockData) {
      let filtered: AccountGroupItem[] = [];
      if (filters?.search && filters.search.trim()) {
        const q = filters.search.toLowerCase().trim();
        filtered = filtered.filter((g) => g.name.toLowerCase().includes(q));
      }
      return of(filtered);
    }

    const params = this.buildFilterParams(filters);
    return this.http
      .get<any>(this.ENDPOINTS.byAccountSummary, { params })
      .pipe(
        map((res) => this.mapAccountSummaryResponse(res)),
        catchError((err) => {
          console.warn('API getByAccountSummary call failed, falling back to mock data:', err);
          return of([]);
        })
      );
  }

  /**
   * Explicit alias for API 2: getByAccountSummary
   */
  public getByAccountSummary(filters?: NonMovingStockFilter): Observable<AccountGroupItem[]> {
    return this.getAccountGroups(filters);
  }

  /**
   * 5. Get By Category Groups Data (Cards & Details Table)
   */
  public getCategoryGroups(filters?: NonMovingStockFilter): Observable<CategoryGroupItem[]> {
    if (this.useMockData) {
      return of([]);
    }

    const params = this.buildFilterParams(filters);
    return this.http
      .get<any>(this.ENDPOINTS.byCategory, { params })
      .pipe(
        map((res) => this.mapCategoryGroupsResponse(res)),
        catchError((err) => {
          console.warn('API getByCategorySummary call failed, falling back to mock data:', err);
          return of([]);
        })
      );
  }

  /**
   * Explicit alias for API: getByCategorySummary
   */
  public getByCategorySummary(filters?: NonMovingStockFilter): Observable<CategoryGroupItem[]> {
    return this.getCategoryGroups(filters);
  }

  /**
   * 6. Get Category Breakdown (Middle Card)
   */
  public getCategoryBreakdown(filters?: NonMovingStockFilter): Observable<CategorySummary[]> {
    if (this.useMockData) {
      return of([]);
    }

    const params = this.buildFilterParams(filters);
    return this.http
      .get<any>(this.ENDPOINTS.categoryBreakdown, { params })
      .pipe(
        map((res) => this.mapCategoryBreakdownResponse(res)),
        catchError((err) => {
          console.warn('API getCategoryBreakdown call failed, falling back to mock data:', err);
          return of([]);
        })
      );
  }

  /**
   * 7. Get Filter Options (TDS List & Account List)
   */
  public getFilterOptions(): Observable<NonMovingStockFilterOptions> {
    if (this.useMockData) {
      return of({} as NonMovingStockFilterOptions);
    }

    return this.http
      .get<any>(this.ENDPOINTS.filterOptions)
      .pipe(
        map((res) => res?.result ?? res),
        catchError(this.errorHandler.handleError)
      );
  }

  /**
   * 8. Export Clearance Report
   */
  public exportReport(filters?: NonMovingStockFilter): Observable<any> {
    if (this.useMockData) {
      return of({ success: true, message: 'Report exported successfully (Mock)' });
    }

    const params = this.buildFilterParams(filters);
    return this.http
      .get<any>(this.ENDPOINTS.export, { params, responseType: 'blob' as 'json' })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * 9. Get All Accounts for Account Filter List
   */
  public getAllAccounts(month?: string, year?: string): Observable<any> {
    let params = new HttpParams();
    if (month && month !== 'All') {
      const normalizedMonth = this.normalizeMonth(month);
      if (normalizedMonth) {
        params = params.set('month', normalizedMonth);
      } else {
        params = params.set('month', month);
      }
    }
    if (year && year !== 'All') {
      params = params.set('year', year);
    }

    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/notMovingStock/getAllAccounts`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }
}

